import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Tags, Loader2, ChevronDown, ChevronRight, RotateCcw, AlertTriangle, Check, Merge,
} from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { SuggestInput } from "./SuggestInput";
import { API } from "../../App";

// Supplier names are free text, so the same supplier ends up spelled
// several ways. This lists them with usage counts, suggests the likely
// duplicates, and merges them in one dry-run-first, undoable pass.
const MergeRow = ({ name, count, checked, disabled, onToggle, testId }) => (
  <label
    className={`flex items-center gap-2 text-sm rounded px-2 py-1.5 ${
      disabled ? "opacity-50" : "hover:bg-slate-800/60 cursor-pointer"
    }`}
  >
    <input
      type="checkbox"
      className="accent-saturn-orange"
      checked={checked}
      disabled={disabled}
      onChange={() => onToggle(name)}
      data-testid={testId}
    />
    <span className="text-white break-all">{name}</span>
    <span className="text-xs text-slate-500">{count} record{count === 1 ? "" : "s"}</span>
  </label>
);

const SupplierNamesCard = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [merges, setMerges] = useState([]);
  const [selected, setSelected] = useState([]);
  const [target, setTarget] = useState("");
  const [preview, setPreview] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [namesRes, mergesRes] = await Promise.all([
        axios.get(`${API}/stock/admin/supplier-names`),
        axios.get(`${API}/stock/admin/supplier-names/merges?limit=10`).catch(() => ({ data: {} })),
      ]);
      setData(namesRes.data);
      setMerges(mergesRes.data?.merges || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load supplier names");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !data) load();
  }, [open, data, load]);

  const vendors = data?.vendors || [];
  const groups = data?.suggested_groups || [];
  const sources = useMemo(
    () => selected.filter(n => n !== target.trim()),
    [selected, target]
  );

  const toggle = (name) => {
    setPreview(null);
    setSelected(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const applyGroup = (group) => {
    setPreview(null);
    setTarget(group.suggested_canonical);
    setSelected(group.members.map(m => m.name));
  };

  const reset = () => {
    setSelected([]);
    setTarget("");
    setPreview(null);
  };

  const runMerge = async (dryRun) => {
    if (!target.trim() || sources.length === 0) {
      toast.error("Pick the names to merge and the correct name to keep");
      return;
    }
    setIsBusy(true);
    try {
      const res = await axios.post(
        `${API}/stock/admin/supplier-names/merge?dry_run=${dryRun}`,
        { from_names: sources, to_name: target.trim() }
      );
      if (dryRun) {
        setPreview(res.data);
        toast.success(`${res.data.records_to_update} record(s) would change`);
      } else {
        toast.success(
          `Merged into "${res.data.to_name}" — ${res.data.records_updated} record(s) updated`
        );
        reset();
        setData(null);
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setIsBusy(false);
    }
  };

  const undo = async (mergeId) => {
    setIsBusy(true);
    try {
      const res = await axios.post(
        `${API}/stock/admin/supplier-names/merges/${mergeId}/undo`
      );
      toast.success(`Undone — ${res.data.records_restored} record(s) restored`);
      setData(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-700" data-testid="supplier-names-card">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center justify-between gap-3 w-full text-left"
          data-testid="supplier-names-toggle"
        >
          <CardTitle className="text-lg font-rajdhani text-white flex items-center gap-2 min-w-0">
            {open ? <ChevronDown className="w-4 h-4 text-slate-400" />
                  : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <Tags className="w-5 h-5 text-saturn-orange flex-shrink-0" />
            <span className="truncate">Supplier Names</span>
          </CardTitle>
          <span className="text-sm text-slate-400 whitespace-nowrap" data-testid="supplier-names-summary">
            {data
              ? `${data.distinct_count} names • ${groups.length} possible duplicate${groups.length === 1 ? "" : "s"}`
              : "Clean up typos & duplicates"}
          </span>
        </button>
      </CardHeader>

      {open && (
        <CardContent className="space-y-5">
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-saturn-orange" />
            </div>
          )}

          {!isLoading && data && (
            <>
              <p className="text-xs text-slate-500">
                Renaming updates every stock record using that supplier. Preview first —
                nothing is written until you apply, and a merge can be undone for{" "}
                {data.undo_window_days} days.
              </p>

              {groups.length > 0 && (
                <div className="space-y-3" data-testid="supplier-suggested-groups">
                  <Label className="text-slate-300">Likely duplicates</Label>
                  {groups.map(g => (
                    <div
                      key={g.group_key}
                      className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3"
                      data-testid={`supplier-group-${g.group_key}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-white text-sm break-words">
                            {g.members.map(m => m.name).join("  ·  ")}
                          </p>
                          <p className="text-xs text-amber-300/80 mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {g.reason_labels.join(" / ")} • {g.total_count} records
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-600 text-slate-200 hover:bg-slate-700"
                          onClick={() => applyGroup(g)}
                          data-testid={`supplier-group-fix-${g.group_key}`}
                        >
                          <Merge className="w-3 h-3 mr-1" />
                          Fix these
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">
                  All supplier names ({vendors.length})
                </Label>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-700 divide-y divide-slate-800">
                  {vendors.length === 0 && (
                    <p className="text-sm text-slate-500 p-3">
                      No supplier names recorded yet.
                    </p>
                  )}
                  {vendors.map(v => (
                    <MergeRow
                      key={v.name}
                      name={v.name}
                      count={v.count}
                      checked={selected.includes(v.name)}
                      disabled={false}
                      onToggle={toggle}
                      testId={`supplier-select-${v.name}`}
                    />
                  ))}
                </div>
                {data.units_without_vendor > 0 && (
                  <p className="text-xs text-slate-500">
                    {data.units_without_vendor} stock record(s) have no supplier set.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Keep this name</Label>
                <SuggestInput
                  value={target}
                  onChange={(v) => { setTarget(v); setPreview(null); }}
                  suggestions={vendors}
                  placeholder="Correct supplier name"
                  testId="supplier-target-name"
                  emptyHint="No supplier names saved yet"
                />
                <p className="text-xs text-slate-500" data-testid="supplier-merge-plan-line">
                  {sources.length === 0
                    ? "Tick the misspelled names above, then set the name to keep."
                    : `${sources.length} name(s) → "${target.trim() || "…"}"`}
                </p>
              </div>

              {preview && (
                <div
                  className="rounded-lg border border-slate-700 bg-slate-800/40 p-3 space-y-1"
                  data-testid="supplier-merge-preview"
                >
                  <p className="text-sm text-white">
                    Preview: {preview.records_to_update} record(s) would become “{preview.to_name}”
                    {preview.target_is_new_name && " (a name not currently in use)"}
                  </p>
                  {preview.per_name.map(p => (
                    <p key={p.name} className="text-xs text-slate-400">
                      {p.name} → {p.total} record(s)
                    </p>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-700"
                  disabled={isBusy || sources.length === 0 || !target.trim()}
                  onClick={() => runMerge(true)}
                  data-testid="supplier-preview-btn"
                >
                  {isBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Preview changes
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  disabled={isBusy || !preview || sources.length === 0 || !target.trim()}
                  onClick={() => runMerge(false)}
                  data-testid="supplier-apply-btn"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Apply merge
                </Button>
                {(selected.length > 0 || target) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-400 hover:text-white"
                    onClick={reset}
                    data-testid="supplier-reset-btn"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {merges.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-slate-800">
                  <Label className="text-slate-300">Recent merges</Label>
                  {merges.map(m => (
                    <div
                      key={m.merge_id}
                      className="flex flex-wrap items-center justify-between gap-2 text-sm"
                      data-testid={`supplier-merge-row-${m.merge_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-white break-words">
                          {(m.from_names || []).join(", ")} → {m.to_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {m.created_at ? new Date(m.created_at).toLocaleString() : ""} •{" "}
                          {m.records_updated} record(s)
                          {m.undone ? " • undone" : ""}
                        </p>
                      </div>
                      {m.can_undo && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-600 text-slate-200 hover:bg-slate-700"
                          disabled={isBusy}
                          onClick={() => undo(m.merge_id)}
                          data-testid={`supplier-undo-${m.merge_id}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Undo
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default SupplierNamesCard;
